import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    // Check for multi-select variant picker - it might be outside the form
    const target = this.closest('.shopify-section, dialog, product-card');
    const multiSelectContainer = target?.querySelector('.variant-option--image-grid-multiselect');
    
    if (multiSelectContainer) {
      // Handle multi-select add to cart
      this.#handleMultiSelectSubmit(event, form, multiSelectContainer);
      return;
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
          
          // Update Vue cart store and show cart drawer
          if (window.store && typeof window.store.getCart === 'function') {
            window.store.getCart().then(() => {
              if (window.SuperCart && typeof window.SuperCart.ShowCartfn === 'function') {
                window.SuperCart.ShowCartfn();
              }
            });
          } else if (window.SuperCart && typeof window.SuperCart.ShowCartfn === 'function') {
            window.SuperCart.ShowCartfn();
          }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Handles multi-select variant submission
   * @param {Event} event - The submit event
   * @param {HTMLFormElement} form - The form element
   * @param {Element} multiSelectContainer - The multi-select container element
   */
  #handleMultiSelectSubmit(event, form, multiSelectContainer) {
    const { addToCartTextError } = this.refs;
    
    // Get all grid items with quantities > 0
    const gridItems = multiSelectContainer.querySelectorAll('.variant-option__grid-item[data-quantity]:not([data-quantity="0"])');
    
    if (gridItems.length === 0) {
      // No items selected, show error
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const errorMessage = 'Please select at least one item';
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          const newTextNode = document.createTextNode(errorMessage);
          addToCartTextError.appendChild(newTextNode);
        }
        
        this.#setLiveRegionText(errorMessage);
        
        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, 5000);
      }
      return;
    }
    
    // Get the product data
    const productId = this.dataset.productId;
    
    // Build array of items to add
    const itemsToAdd = [];
    // Find the variant picker - it's a sibling, not an ancestor
    const target = this.closest('.shopify-section, dialog, product-card');
    const variantPicker = target?.querySelector('variant-picker-vue');
    
    if (!variantPicker || !variantPicker.__vueApp) {
      console.error('Vue variant picker not found');
      return;
    }
    
    // Get the Vue app instance to access product data
    const vueApp = variantPicker.__vueApp;
    const productData = JSON.parse(variantPicker.querySelector('script[data-product-data]')?.textContent || '{}');
    
    // Determine the purchase type (Subscribe & Save vs One Time)
    const subscribeRadio = target?.querySelector('input[type="radio"][value*="Subscribe"]');
    const isSubscription = subscribeRadio?.checked;
    const purchaseType = isSubscription ? 'Subscribe & Save' : 'One Time';
    
    gridItems.forEach(gridItem => {
      const optionValue = gridItem.dataset.optionValue;
      const quantity = parseInt(gridItem.dataset.quantity || '0');
      
      if (quantity > 0) {
        // Find the variant that matches this flavor AND purchase type
        const variant = productData.variants.find(v => {
          const matchesFlavor = v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue;
          const matchesPurchaseType = v.option1 === purchaseType || v.option2 === purchaseType || v.option3 === purchaseType;
          return matchesFlavor && matchesPurchaseType;
        }) || productData.variants.find(v => 
          // Fallback: if no exact match, find any variant with this flavor
          v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue
        );
        
        if (variant && variant.available) {
          itemsToAdd.push({
            id: variant.id,
            quantity: quantity
          });
        }
      }
    });
    
    if (itemsToAdd.length === 0) {
      // No available items to add
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const errorMessage = 'Selected items are not available';
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          const newTextNode = document.createTextNode(errorMessage);
          addToCartTextError.appendChild(newTextNode);
        }
        
        this.#setLiveRegionText(errorMessage);
        
        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, 5000);
      }
      return;
    }
    
    // Get selling plan if subscription is selected
    let sellingPlan = '';
    if (isSubscription) {
      // Find the delivery frequency select (it has the selling plan ID as its value)
      const deliverySelect = target?.querySelector('select:not([name="id"])');
      sellingPlan = deliverySelect?.value || '';
    }
    
    // Prepare the request body for multiple items
    const requestBody = {
      items: itemsToAdd.map(item => ({
        id: item.id,
        quantity: item.quantity,
        ...(sellingPlan && { selling_plan: sellingPlan })
      }))
    };
    
    // Get cart sections for update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    
    if (cartItemComponentsSectionIds.length > 0) {
      requestBody.sections = cartItemComponentsSectionIds.join(',');
    }
    
    // Make the cart add request
    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message || response.description));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message || response.description;
          } else {
            const newTextNode = document.createTextNode(response.message || response.description);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(response.message || response.description);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: itemsToAdd.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          // Add aria-live region to inform screen readers that items were added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }
          
          // Animate add to cart button
          this.refs.addToCartButtonContainer?.animateAddToCart();

          const totalQuantity = itemsToAdd.reduce((sum, item) => sum + item.quantity, 0);
          
          this.dispatchEvent(
            new CartAddEvent({}, itemsToAdd[0].id.toString(), {
              source: 'product-form-component',
              itemCount: totalQuantity,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
          
          // Reset quantities to 0 after successful add
          gridItems.forEach(gridItem => {
            gridItem.dataset.quantity = '0';
            const quantityDisplay = gridItem.querySelector('.variant-option__quantity-display');
            if (quantityDisplay) quantityDisplay.textContent = '0';
            
            const minusBtn = gridItem.querySelector('.variant-option__quantity-btn--minus');
            if (minusBtn) {
              minusBtn.disabled = true;
              minusBtn.classList.add('opacity-30', 'cursor-not-allowed');
              minusBtn.classList.remove('cursor-pointer');
            }
            
            const quantitySelector = gridItem.querySelector('.variant-option__quantity-selector');
            if (quantitySelector) {
              quantitySelector.classList.remove('bg-white', 'border-2');
              quantitySelector.classList.add('bg-transparent');
            }
          });
          
          // Update Vue cart store and show cart drawer
          if (window.store && typeof window.store.getCart === 'function') {
            window.store.getCart().then(() => {
              if (window.SuperCart && typeof window.SuperCart.ShowCartfn === 'function') {
                window.SuperCart.ShowCartfn();
              }
            });
          } else if (window.SuperCart && typeof window.SuperCart.ShowCartfn === 'function') {
            window.SuperCart.ShowCartfn();
          }
        }
      })
      .catch((error) => {
        console.error('Multi-select add to cart error:', error);
        
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const errorMessage = 'An error occurred. Please try again.';
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }
          
          this.#setLiveRegionText(errorMessage);
          
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);
        }
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html?.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    // this.refs.addToCartButtonContainer?.disable();
  }; 
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
